import { useAppRouteNavigation } from "@/app/app/_/routing";
import { Navigation } from "@/constants/navigation";
import { Values } from "@/constants";
import { iconsByTypename } from "@/objects/@icons";
import { State } from "@/state";
import { AppViewsType } from "@/state/@types";
import _ from "lodash";
import { useLayoutEffect, useRef } from "react";
import {
  ChartColumnIcon,
  ListChecksIcon,
  MessageSquareTextIcon,
  NetworkIcon,
  WalletIcon,
  type LucideIcon,
} from "lucide-react";

export type NavigateAppViewContextType = Record<string, never>;

/** A view's name as the sidebar gives it. */
export function displayNameForView(view: AppViewsType): string {
  const item = Navigation.SIDEBAR.find(
    (entry) => entry.type === "routing" && entry.key === view,
  );
  if (item && item.type === "routing" && "name" in item) {
    return item.name;
  }
  return _.capitalize(view.replace(/-/g, " "));
}

const VIEW_ICONS: Record<AppViewsType, LucideIcon> = {
  triage: ListChecksIcon,
  budget: WalletIcon,
  reports: ChartColumnIcon,
  thread: MessageSquareTextIcon,
  transactions: iconsByTypename.TransactionObject,
  "financial-accounts": iconsByTypename.FinancialAccountObject,
  "journal-entries": iconsByTypename.JournalEntryObject,
  "chart-of-accounts": iconsByTypename.ChartedAccountObject,
  documents: iconsByTypename.DocumentObject,
  contacts: iconsByTypename.ContactObject,
  organizations: iconsByTypename.OrganizationObject,
  "tax-codes": iconsByTypename.TaxObject,
  rules: NetworkIcon,
};

/** Extra phrases so command search matches common synonyms (e.g. "dashboard" → Reports). */
const VIEW_SEARCH_HINTS: Partial<Record<AppViewsType, string>> = {
  reports: "Dashboard layouts cash flow profit loss balance sheet",
  thread: "Thread AI assistant",
  transactions: "Bank feed ledger",
  "financial-accounts": "Banks cards accounts",
  "chart-of-accounts": "COA GL accounts",
  documents: "Files upload",
  contacts: "Customers suppliers vendors employees",
  "tax-codes": "Taxes GST HST PST VAT sales tax rates",
  rules: "Automation rules bank transaction",
  budget: "Plan monthly spending left to spend bills savings goals",
};

const ACTION_VIEWS: Set<AppViewsType> = new Set(["triage", "rules"]);

export function NavigateAppViewCommands() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );
  const appRouteNavigation = useAppRouteNavigation();
  const appRouteNavigationRef = useRef(appRouteNavigation);
  appRouteNavigationRef.current = appRouteNavigation;

  useLayoutEffect(() => {
    const views = (
      Object.keys(Values.APP_VIEW_TAB_MODE_MAP) as AppViewsType[]
    ).filter((view) => view !== "budget" || Values.IS_BUDGET_SHOWN);

    for (const view of views) {
      const id = `navigate-app-view-${view}`;
      const placeName = displayNameForView(view);
      const hints = VIEW_SEARCH_HINTS[view];
      const isAction = ACTION_VIEWS.has(view);
      const groupName = isAction ? "Actions" : "Objects";
      registerCommandDefinition({
        id,
        title: (
          <>
            <span style={{ color: "var(--muted-foreground)" }}>
              {groupName} &gt;
            </span>{" "}
            <span>{placeName}</span>
          </>
        ),
        searchText: [placeName, groupName, hints].filter(Boolean).join(" "),
        description: hints
          ? `Open the ${placeName} view. ${hints}`
          : `Open the ${placeName} view.`,
        onClick: () => {
          appRouteNavigationRef.current.navigateToView(view, {
            preservePanel: true,
            preserveQuery: false,
          });
          State.getState().clearCommandStack();
        },
        ReactIcon: VIEW_ICONS[view],
        group: groupName,
        groupPriority: 100,
      });
    }

    return () => {
      for (const view of views) {
        unregisterCommandDefinition(`navigate-app-view-${view}`);
      }
    };
  }, [registerCommandDefinition, unregisterCommandDefinition]);

  return null;
}
